import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";

import { adminGet } from "@lib/adminApi";

import { formatBytes, formatMoney } from "./healthFormat";
import { ErrorLine, Panel, REFRESH_MS, RefreshFailed } from "./panelParts";
import type { Effort, Impact, PrioritiesResponse, PriorityItem } from "./types";

// The "most worth fixing" list at the top of the Health page (OMG-2798):
// everything on the page that is outside its band, ranked by impact (how far
// past Google's good band, weighted by the traffic and revenue the page
// carries; how often a monitor fails; purchases below the usual weekday) with
// a cheap / medium / expensive effort bucket. Each row opens to why it scored
// and the detail behind it. omg-api does the ranking; this only draws it.

const SHOWN = 8;

const IMPACT_STYLE: Record<Impact, string> = {
  high: "bg-red-100 text-red-800",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-gray-100 text-gray-700",
};

const EFFORT_STYLE: Record<Effort, string> = {
  cheap: "bg-green-100 text-green-800",
  medium: "bg-amber-100 text-amber-800",
  expensive: "bg-gray-200 text-gray-800",
};

const KIND_LABEL: Record<PriorityItem["kind"], string> = {
  perf: "Performance",
  monitor: "Monitor",
  purchases: "Purchases",
};

const Badge = ({ className, text }: { className: string; text: string }) => (
  <span
    className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${className}`}
  >
    {text}
  </span>
);

const RowLink = ({ item }: { item: PriorityItem }) => {
  if (!item.link) return null;
  if (item.link.startsWith("/")) {
    return (
      <Link to={item.link} className="text-xs text-blue-700 hover:underline">
        Open
      </Link>
    );
  }
  return (
    <a
      href={item.link}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
    >
      {item.kind === "monitor" ? "Run" : "Page"} <ExternalLink size={12} />
    </a>
  );
};

const Detail = ({ item }: { item: PriorityItem }) => {
  const d = item.detail;
  return (
    <div className="text-xs text-gray-700 space-y-2 py-2 pl-2 border-l-2 border-gray-200">
      <p>
        <span className="font-semibold">Impact:</span> {item.impactWhy}
      </p>
      <p>
        <span className="font-semibold">Effort:</span> {item.effortWhy}
      </p>
      {d.kind === "perf" && (
        <>
          <p className="text-gray-500">
            Weights: traffic {d.weights.traffic}, revenue {d.weights.revenue}{" "}
            (static table in omg-api)
          </p>
          {d.lcpElement && (
            <p>
              Largest paint: {d.lcpElement.tag ?? "element"}
              {d.lcpElement.src && (
                <span className="font-mono"> {d.lcpElement.src}</span>
              )}
              {d.lcpElement.loading === "lazy" && (
                <span className="text-red-700"> (lazy-loaded)</span>
              )}
            </p>
          )}
          {d.findings.length > 0 && (
            <ul className="space-y-0.5">
              {d.findings.map((f, i) => (
                <li
                  key={i}
                  className={
                    f.severity === "warn"
                      ? "text-amber-700"
                      : f.known
                        ? "text-gray-500"
                        : "text-red-700"
                  }
                >
                  {f.message}
                </li>
              ))}
            </ul>
          )}
          {d.probe && d.probe.renderBlocking.length > 0 && (
            <p>
              Render-blocking:{" "}
              {d.probe.renderBlocking
                .map((r) => `${r.url} (${formatBytes(r.bytes)})`)
                .join(", ")}
            </p>
          )}
          {d.probe && d.probe.heaviest.length > 0 && (
            <p>
              Heaviest:{" "}
              {d.probe.heaviest
                .map((r) => `${r.url} (${formatBytes(r.bytes)})`)
                .join(", ")}
            </p>
          )}
        </>
      )}
      {d.kind === "monitor" && (
        <p>
          Last {d.monitor.history.length} runs:{" "}
          {d.monitor.history.map((c) => c.replace("_", " ")).join(", ")}.
          {d.monitor.latest.summary && ` Latest: ${d.monitor.latest.summary}`}
        </p>
      )}
      {d.kind === "purchases" && (
        <p>
          {d.todayCount} purchases so far against an average of {d.averageCount}
          {d.changePercent !== null && ` (${d.changePercent}%)`}
          {d.revenueAtStake &&
            `; about ${formatMoney(d.revenueAtStake.currency, d.revenueAtStake.amount)} of usual revenue not seen yet`}
          .
        </p>
      )}
    </div>
  );
};

export const PrioritiesPanel = () => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["admin-health-priorities"] as const,
    queryFn: () => adminGet<PrioritiesResponse>("/admin/health/priorities"),
    refetchInterval: REFRESH_MS,
  });
  const title = "Most worth fixing";

  if (isLoading) return <Panel title={title}>Loading...</Panel>;
  if (!data)
    return (
      <Panel title={title}>
        <ErrorLine error={error} />
      </Panel>
    );

  const shown = data.items.slice(0, SHOWN);
  const hidden = data.items.length - shown.length;

  return (
    <Panel title={title}>
      {error && <RefreshFailed error={error} />}
      {data.items.length === 0 ? (
        <p className="text-sm text-green-700">
          Nothing is outside its band: every measured page is in the good band,
          every monitor is green, and purchases look normal.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b">
              <th className="py-2 pr-2 w-8">#</th>
              <th className="py-2 pr-2">What</th>
              <th className="py-2 pr-2">Impact</th>
              <th className="py-2 pr-2">Effort</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {shown.map((item, i) => (
              <tr key={item.id} className="border-b last:border-0 align-top">
                <td className="py-2 pr-2 text-gray-400">{i + 1}</td>
                <td className="py-2 pr-2">
                  <details className="group">
                    <summary className="cursor-pointer select-none list-none">
                      <span className="font-medium">{item.title}</span>
                      <span className="text-xs text-gray-400">
                        {" "}
                        · {KIND_LABEL[item.kind]} ·{" "}
                        <span className="text-blue-700">
                          <span className="group-open:hidden">why</span>
                          <span className="hidden group-open:inline">hide</span>
                        </span>
                      </span>
                    </summary>
                    <Detail item={item} />
                  </details>
                </td>
                <td className="py-2 pr-2 whitespace-nowrap">
                  <Badge
                    className={IMPACT_STYLE[item.impact]}
                    text={item.impact}
                  />
                  <span className="text-xs text-gray-400 ml-1">
                    {Math.round(item.score)}
                  </span>
                </td>
                <td className="py-2 pr-2 whitespace-nowrap">
                  <Badge
                    className={EFFORT_STYLE[item.effort]}
                    text={item.effort}
                  />
                </td>
                <td className="py-2">
                  <RowLink item={item} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {hidden > 0 && (
        <p className="text-xs text-gray-400 mt-1">
          {hidden} more below the line; the sections underneath have them all.
        </p>
      )}
      <p className="text-xs text-gray-400 mt-2">
        Impact: distance past Google&apos;s good band, weighted by the traffic
        and revenue the page carries (a static table, not live page views);
        monitors by how often they fail; purchases when a day is 30% or more
        below the usual weekday
        {data.purchasesIncluded ? "" : " (not enabled for this account)"}.
        Effort is read off the probe&apos;s findings: an image fix is cheap, a
        tag or caching change is medium, bundle work is expensive.
      </p>
    </Panel>
  );
};
